/* Everything about a document that is not one of its fields.
 *
 * This is the difference between a form renderer and an ERP. When two people
 * disagree about an invoice, what settles it is the record of who changed
 * what and when, what was said about it, and what else points at it — not
 * asking around.
 *
 * Frappe keeps all of this and hands it over with the document, so nothing
 * here costs an extra request on open. The work is in making a version row
 * readable: Frappe stores `[["status","Draft","Submitted"]]`, and a person
 * needs "Priya changed Status from Draft to Submitted".
 */
import { useEffect, useState } from 'react'
import { useErp } from './context'
import { formatSince } from './format'
import type { ErpError } from './api'

type DocInfo = {
  comments?: {
    name: string; content?: string; creation?: string
    /** `getdoc` returns `owner`; the add-comment reply returns `comment_by`
     *  and `comment_email`. All three are read, or the author shows as
     *  "Someone" on every comment already on the document. */
    owner?: string; comment_email?: string; comment_by?: string
  }[]
  versions?: { name: string; owner?: string; creation?: string; data?: string }[]
  assignments?: { name: string; owner?: string; description?: string; status?: string }[]
  attachments?: { name: string; file_name?: string; file_url?: string; creation?: string }[]
  communications?: { name: string; subject?: string; creation?: string; sender?: string }[]
  tags?: string
}

type Tab = 'activity' | 'attachments' | 'connections' | 'shared'

export function Activity({ doctype, name, docinfo, onChanged, canWrite }: {
  doctype: string
  name: string
  docinfo: DocInfo
  onChanged: () => void
  canWrite: boolean
}) {
  const [tab, setTab] = useState<Tab>('activity')
  const counts = {
    activity: (docinfo.comments?.length ?? 0) + (docinfo.versions?.length ?? 0),
    attachments: docinfo.attachments?.length ?? 0,
    connections: undefined as number | undefined,
    shared: undefined as number | undefined,
  }

  return (
    <section className="card" style={{ overflow: 'hidden' }}>
      <div className="row" style={{ gap: 2, borderBottom: '1px solid var(--line)', padding: '4px 6px', flexWrap: 'wrap' }}>
        {(['activity', 'attachments', 'connections', 'shared'] as Tab[]).map((t) => (
          <button key={t} className="btn btn-quiet btn-sm" aria-current={t === tab ? 'true' : undefined}
                  style={t === tab ? { background: 'var(--accent-soft)', color: 'var(--accent)' } : undefined}
                  onClick={() => setTab(t)}>
            {t === 'activity' ? 'Activity' : t === 'attachments' ? 'Attachments'
              : t === 'connections' ? 'Connections' : 'Shared with'}
            {counts[t] ? <span className="chip" style={{ marginLeft: 4 }}>{counts[t]}</span> : null}
          </button>
        ))}
        <span className="grow" />
        <Tags doctype={doctype} name={name} tags={docinfo.tags} canWrite={canWrite} onChanged={onChanged} />
      </div>

      <div style={{ padding: 14 }}>
        {tab === 'activity' ? (
          <ActivityStream doctype={doctype} name={name} docinfo={docinfo}
                          onChanged={onChanged} canWrite={canWrite} />
        ) : tab === 'attachments' ? (
          <Attachments docinfo={docinfo} />
        ) : tab === 'connections' ? (
          <Connections doctype={doctype} name={name} />
        ) : (
          <SharedWith doctype={doctype} name={name} canWrite={canWrite} />
        )}
      </div>
    </section>
  )
}

/* ── the stream: comments and the audit trail, newest first ─────────── */

type Entry = { at: string; who: string; kind: 'said' | 'changed'; body: React.ReactNode }

function ActivityStream({ doctype, name, docinfo, onChanged, canWrite }: {
  doctype: string; name: string; docinfo: DocInfo; onChanged: () => void; canWrite: boolean
}) {
  const erp = useErp()
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const entries: Entry[] = [
    ...(docinfo.comments ?? []).map((c) => ({
      at: c.creation ?? '',
      who: c.comment_by || c.comment_email || c.owner || 'Someone',
      kind: 'said' as const,
      body: <div dangerouslySetInnerHTML={{ __html: stripTags(c.content ?? '') }} />,
    })),
    ...(docinfo.versions ?? []).map((v) => ({
      at: v.creation ?? '',
      who: v.owner || 'Someone',
      kind: 'changed' as const,
      body: <Changes data={v.data} />,
    })),
  ].sort((a, b) => (b.at > a.at ? 1 : -1))

  const add = async () => {
    const content = draft.trim()
    if (!content) return
    setSaving(true); setFailed(null)
    try {
      await erp.addComment(doctype, name, content)
      setDraft('')
      onChanged()
    } catch (e) {
      setFailed((e as ErpError).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="stack" style={{ gap: 14 }}>
      {canWrite ? (
        <div className="stack" style={{ gap: 6 }}>
          <textarea className="textarea" rows={2} value={draft} placeholder="Add a note about this document… use @ to mention somebody"
                    aria-label="Add a comment" onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void add() }} />
          <Mentions text={draft} onPick={(who) => setDraft((d) => d.replace(/@\S*$/, `@${who} `))} />
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-sm btn-primary" onClick={add} disabled={saving || !draft.trim()}>
              {saving ? 'Adding…' : 'Add note'}
            </button>
            <span className="small muted">Ctrl + Enter</span>
            {failed ? <span className="small err">{failed}</span> : null}
          </div>
        </div>
      ) : null}

      <Assignments doctype={doctype} name={name} docinfo={docinfo}
                   canWrite={canWrite} onChanged={onChanged} />

      {entries.length === 0 ? (
        <p className="small muted" style={{ margin: 0 }}>Nothing has happened to this document yet.</p>
      ) : (
        <ol className="stack" style={{ listStyle: 'none', margin: 0, padding: 0, gap: 12 }}>
          {entries.map((e, i) => (
            <li key={i} className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
              <span className={`chip ${e.kind === 'said' ? 'chip-info' : ''}`} style={{ marginTop: 2 }}>
                {e.kind === 'said' ? 'note' : 'change'}
              </span>
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="small muted">
                  <b style={{ color: 'var(--ink-2)' }}>{e.who}</b> · <time dateTime={e.at} title={e.at}>{formatSince(e.at)}</time>
                </div>
                <div className="small" style={{ marginTop: 2 }}>{e.body}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

/** Frappe stores a version as `{"changed":[[field, from, to]], "added":[], ...}`.
 *  A person needs a sentence, and an old value beside the new one. */
function Changes({ data }: { data?: string }) {
  let parsed: { changed?: [string, unknown, unknown][]; added?: unknown[]; removed?: unknown[]; row_changed?: unknown[] }
  try {
    parsed = JSON.parse(data ?? '{}')
  } catch {
    return <span className="muted">changed something</span>
  }
  const changed = (parsed.changed ?? []).filter(([field]) => field !== 'modified_by')
  if (changed.length === 0) {
    const rows = (parsed.added?.length ?? 0) + (parsed.removed?.length ?? 0) + (parsed.row_changed?.length ?? 0)
    return <span className="muted">{rows ? `changed ${rows} row${rows === 1 ? '' : 's'} in a table` : 'saved it'}</span>
  }
  return (
    <div className="stack" style={{ gap: 2 }}>
      {changed.slice(0, 12).map(([field, from, to], i) => (
        <div key={i}>
          <b>{humanise(field)}</b>{' '}
          <span className="muted" style={{ textDecoration: 'line-through' }}>{show(field, from)}</span>
          {' → '}
          <span>{show(field, to)}</span>
        </div>
      ))}
      {changed.length > 12 ? <div className="muted">and {changed.length - 12} more</div> : null}
    </div>
  )
}

/** `docstatus` is 0/1/2 in the database and Draft/Submitted/Cancelled to a
 *  person. "Docstatus 0 → 1" is the database talking. */
const DOCSTATUS_WORDS = ['Draft', 'Submitted', 'Cancelled']

const show = (field: string, v: unknown) => {
  if (field === 'docstatus') return DOCSTATUS_WORDS[Number(v)] ?? String(v)
  if (v === null || v === undefined || v === '') return '(empty)'
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 60)
  return String(v).slice(0, 80)
}

const humanise = (field: string) =>
  field === 'docstatus' ? 'Status'
    : field.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())

/**
 * Who you can mention, while you are typing an `@`.
 *
 * The mention is left in the text as `@somebody@example.com`. Frappe's own
 * parser turns that into a notification when the comment is saved, so this
 * only has to help somebody type the address correctly — inventing our own
 * mention markup would be a second syntax nobody else understands.
 */
function Mentions({ text, onPick }: { text: string; onPick: (who: string) => void }) {
  const erp = useErp()
  const [people, setPeople] = useState<{ name: string; full_name?: string }[]>([])
  const partial = /@(\S*)$/.exec(text)?.[1]

  useEffect(() => {
    if (partial === undefined) { setPeople([]); return }
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      erp.people(partial, controller.signal).then((r) => setPeople(r.people ?? [])).catch(() => setPeople([]))
    }, 140)
    return () => { controller.abort(); window.clearTimeout(timer) }
  }, [erp, partial])

  if (partial === undefined || people.length === 0) return null
  return (
    <div className="row" style={{ gap: 5, flexWrap: 'wrap' }} role="listbox" aria-label="Mention somebody">
      {people.slice(0, 6).map((p) => (
        <button key={p.name} type="button" className="chip" role="option" aria-selected={false}
                onClick={() => onPick(p.name)}>{p.full_name || p.name}</button>
      ))}
    </div>
  )
}

/** Frappe stores a comment as HTML. It is written by people who can already
 *  write to the document, but `innerHTML` still gets only what cannot run. */
function stripTags(html: string): string {
  return html
    .replace(/<\/?(script|style|iframe|object|embed|link|meta|form|base)\b[^>]*>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/(href|src)\s*=\s*["']?\s*javascript:[^"'\s>]*/gi, '$1="#"')
}

/* ── assignments ────────────────────────────────────────────────────── */

function Assignments({ doctype, name, docinfo, canWrite, onChanged }: {
  doctype: string; name: string; docinfo: DocInfo; canWrite: boolean; onChanged: () => void
}) {
  const erp = useErp()
  const open = (docinfo.assignments ?? []).filter((a) => a.status !== 'Cancelled')
  const [busy, setBusy] = useState(false)

  if (open.length === 0 && !canWrite) return null

  return (
    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
      <span className="small muted">Assigned to</span>
      {open.length === 0 ? <span className="small muted">nobody</span> : open.map((a) => (
        <span key={a.name} className="chip chip-info" title={a.description || undefined}>
          {a.owner}
          {canWrite ? (
            <button type="button" className="btn btn-quiet btn-sm" aria-label={`Unassign ${a.owner}`}
                    style={{ minHeight: 0, padding: '0 2px' }} disabled={busy}
                    onClick={async () => {
                      setBusy(true)
                      try { await erp.unassign(doctype, name, String(a.owner)); onChanged() }
                      finally { setBusy(false) }
                    }}>×</button>
          ) : null}
        </span>
      ))}
    </div>
  )
}

/* ── tags ───────────────────────────────────────────────────────────── */

function Tags({ doctype, name, tags, canWrite, onChanged }: {
  doctype: string; name: string; tags?: string; canWrite: boolean; onChanged: () => void
}) {
  const erp = useErp()
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const list = (tags ?? '').split(',').map((t) => t.trim()).filter(Boolean)

  const add = async () => {
    const label = draft.trim()
    if (!label) { setAdding(false); return }
    try { await erp.addTag(doctype, name, label); setDraft(''); setAdding(false); onChanged() }
    catch { setAdding(false) }
  }

  return (
    <div className="row" style={{ gap: 5, flexWrap: 'wrap' }}>
      {list.map((t) => (
        <span key={t} className="chip">
          {t}
          {canWrite ? (
            <button type="button" className="btn btn-quiet btn-sm" aria-label={`Remove tag ${t}`}
                    style={{ minHeight: 0, padding: '0 2px' }}
                    onClick={async () => { await erp.removeTag(doctype, name, t); onChanged() }}>×</button>
          ) : null}
        </span>
      ))}
      {canWrite ? (
        adding ? (
          <input className="input" style={{ width: 130, minHeight: 26, fontSize: 12 }} autoFocus
                 value={draft} aria-label="New tag" placeholder="tag"
                 onChange={(e) => setDraft(e.target.value)}
                 onBlur={add}
                 onKeyDown={(e) => { if (e.key === 'Enter') void add(); if (e.key === 'Escape') setAdding(false) }} />
        ) : (
          <button className="btn btn-quiet btn-sm" onClick={() => setAdding(true)}>+ tag</button>
        )
      ) : null}
    </div>
  )
}

/* ── attachments and connections ────────────────────────────────────── */

function Attachments({ docinfo }: { docinfo: DocInfo }) {
  const files = docinfo.attachments ?? []
  if (files.length === 0) return <p className="small muted" style={{ margin: 0 }}>Nothing is attached.</p>
  return (
    <ul className="stack" style={{ listStyle: 'none', margin: 0, padding: 0, gap: 4 }}>
      {files.map((f) => (
        <li key={f.name} className="row">
          <a className="grow truncate" href={f.file_url} target="_blank" rel="noreferrer">
            {f.file_name || f.file_url}
          </a>
          <span className="small muted">{formatSince(f.creation)}</span>
        </li>
      ))}
    </ul>
  )
}

/** Access to one document, as distinct from a role.
 *
 * A role says "this person may see invoices"; a share says "this person may
 * see *this* invoice". It is how an approval reaches somebody who does not
 * otherwise work in Accounts.
 */
function SharedWith({ doctype, name, canWrite }: { doctype: string; name: string; canWrite: boolean }) {
  const erp = useErp()
  const [rows, setRows] = useState<{ user: string; write?: number }[] | null>(null)
  const [people, setPeople] = useState<{ name: string; full_name?: string }[]>([])
  const [busy, setBusy] = useState(false)

  const load = () => {
    erp.sharedWith(doctype, name).then((r) => setRows(r.shared ?? [])).catch(() => setRows([]))
  }
  useEffect(load, [erp, doctype, name])
  useEffect(() => { erp.people().then((r) => setPeople(r.people ?? [])).catch(() => setPeople([])) }, [erp])

  if (rows === null) return <p className="small muted" style={{ margin: 0 }}>Looking…</p>

  const already = new Set(rows.map((r) => r.user))
  const spare = people.filter((p) => !already.has(p.name))

  return (
    <div className="stack" style={{ gap: 10 }}>
      {rows.length === 0
        ? <p className="small muted" style={{ margin: 0 }}>
            Only people whose role covers {doctype} can see this.
          </p>
        : (
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {rows.map((r) => (
              <span key={r.user} className="chip chip-info">
                {r.user}{r.write ? ' · can edit' : ''}
                {canWrite ? (
                  <button type="button" className="btn btn-quiet btn-sm" aria-label={`Stop sharing with ${r.user}`}
                          style={{ minHeight: 0, padding: '0 2px' }} disabled={busy}
                          onClick={async () => {
                            setBusy(true)
                            try { await erp.unshare(doctype, name, r.user); load() } finally { setBusy(false) }
                          }}>×</button>
                ) : null}
              </span>
            ))}
          </div>
        )}
      {canWrite && spare.length ? (
        <select className="select" style={{ maxWidth: 240 }} aria-label="Share with" value="" disabled={busy}
                onChange={async (e) => {
                  if (!e.target.value) return
                  setBusy(true)
                  try { await erp.shareWith(doctype, name, e.target.value); load() } finally { setBusy(false) }
                }}>
          <option value="">Share with…</option>
          {spare.map((p) => <option key={p.name} value={p.name}>{p.full_name || p.name}</option>)}
        </select>
      ) : null}
    </div>
  )
}

function Connections({ doctype, name }: { doctype: string; name: string }) {
  const erp = useErp()
  const [groups, setGroups] = useState<{ doctype: string; names: string[]; count: number }[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    setGroups(null); setFailed(false)
    erp.documentLinks(doctype, name, controller.signal)
      .then((r) => setGroups(r.links ?? []))
      .catch((e) => { if ((e as Error).name !== 'AbortError') setFailed(true) })
    return () => controller.abort()
  }, [erp, doctype, name])

  if (failed) return <p className="small muted" style={{ margin: 0 }}>Could not work out what links to this.</p>
  if (groups === null) return <p className="small muted" style={{ margin: 0 }}>Looking…</p>
  if (groups.length === 0) return <p className="small muted" style={{ margin: 0 }}>Nothing else points at this document.</p>

  return (
    <div className="stack" style={{ gap: 10 }}>
      {groups.map((g) => (
        <div key={g.doctype}>
          <div className="small muted" style={{ marginBottom: 3 }}>{g.doctype} · {g.count}</div>
          <div className="row" style={{ gap: 5, flexWrap: 'wrap' }}>
            {g.names.map((n) => (
              <a key={n} className="chip chip-info" href={`/doc/${encodeURIComponent(g.doctype)}/${encodeURIComponent(n)}`}>
                {n}
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
